/**
 * Sync status management for offline operations
 * Handles pendingSync queue and retry logic
 */

import { getItem, setItem, removeItem } from './localStorage';

const PENDING_SYNC_KEY = 'pendingSync';

function notifyPendingSyncChanged() {
  if (typeof window !== 'undefined') {
    window.dispatchEvent(new CustomEvent('pendingSyncChanged'));
  }
}

/**
 * Mark an operation as pending sync
 */
export function markPending(id, operation, data, clientRequestId = null) {
  const pending = getPending();
  pending[id] = {
    operation,
    data,
    timestamp: Date.now(),
    clientRequestId: clientRequestId || id
  };
  setItem(PENDING_SYNC_KEY, pending);
  notifyPendingSyncChanged();
}

/**
 * Get all pending sync operations
 */
export function getPending() {
  return getItem(PENDING_SYNC_KEY, {});
}

/**
 * Clear a specific pending operation
 */
export function clear(id) {
  const pending = getPending();
  delete pending[id];
  setItem(PENDING_SYNC_KEY, pending);
  notifyPendingSyncChanged();
}

/**
 * Clear all pending operations
 */
export function clearAll() {
  removeItem(PENDING_SYNC_KEY);
  notifyPendingSyncChanged();
}

/**
 * Check if there are any pending operations
 */
export function hasPending() {
  const pending = getPending();
  return Object.keys(pending).length > 0;
}

/**
 * Get count of pending operations
 */
export function getPendingCount() {
  const pending = getPending();
  return Object.keys(pending).length;
}

/**
 * Retry all pending operations
 * This should be called when coming back online
 */
export async function retryPending(services) {
  const pending = getPending();
  const ids = Object.keys(pending);

  const results = {
    success: [],
    failed: []
  };

  for (const tempId of ids) {
    const { operation, data, clientRequestId } = pending[tempId];

    try {
      let result;

      if (operation === 'create') {
        // Determine entity type from data
        if (data.projectId !== undefined) {
          // It's a task
          result = await services.taskService.create(data, clientRequestId);
        } else if (data.name && data.color && data.ownerId === undefined) {
          // It's a tag
          result = await services.tagService.create(data, clientRequestId);
        } else if (data.name && data.color && data.ownerId !== undefined) {
          // It's a project
          result = await services.projectService.create(data, clientRequestId);
        }
      } else if (operation === 'update') {
        // Determine entity type and update
        if (data.projectId !== undefined) {
          result = await services.taskService.update(tempId, data);
        } else if (data.ownerId !== undefined) {
          result = await services.projectService.update(tempId, data);
        } else {
          result = await services.tagService.update(tempId, data);
        }
      } else if (operation === 'delete') {
        // Determine entity type and delete
        if (data.projectId !== undefined) {
          result = await services.taskService.delete(tempId);
        } else if (data.ownerId !== undefined) {
          result = await services.projectService.delete(tempId);
        } else {
          result = await services.tagService.delete(tempId);
        }
      }

      if (result && result.success) {
        clear(tempId);
        results.success.push({ tempId, realId: result.data?.id });

        // Dispatch event for ID mapping if it was a create operation
        if (operation === 'create' && result.data?.id) {
          window.dispatchEvent(new CustomEvent('idMappingUpdated', {
            detail: {
              tempId,
              realId: result.data.id,
              entityType: data.projectId !== undefined ? 'task' : (data.ownerId !== undefined ? 'project' : 'tag')
            }
          }));
        }
      } else {
        throw new Error(result?.error || 'Unknown error');
      }
    } catch (error) {
      console.error(`Retry ${operation} ${tempId} failed:`, error);
      results.failed.push({ tempId, error: error.message });
      // Keep in pending queue for next retry
    }
  }

  return results;
}

/**
 * Update local ID mapping after successful sync
 * This replaces temp-* IDs with real UUIDs from Supabase
 */
export function updateLocalIdMapping(tempId, realId, entityType, updateFn) {
  // This function is called by the retry logic to update local state
  // The actual update is handled by listening to the 'idMappingUpdated' event
  if (updateFn && typeof updateFn === 'function') {
    updateFn(tempId, realId, entityType);
  }
}

/**
 * Validate UUID format
 */
export function isValidUUID(str) {
  const uuidRegex = /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(str);
}

/**
 * Check if an ID is a temp ID
 */
export function isTempId(id) {
  return typeof id === 'string' && id.startsWith('temp-');
}

export const syncStatus = {
  markPending,
  getPending,
  clear,
  clearAll,
  hasPending,
  getPendingCount,
  retryPending,
  updateLocalIdMapping,
  isValidUUID,
  isTempId
};
